import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Sparkles, Check, Calendar, Crown, ShieldCheck, Clock } from "lucide-react";

const MembershipChoicePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({ monthly: { price: 9.99 }, annual: { price: 99.99 } });
  const [trialDays, setTrialDays] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/payments/plans`);
        setPlans(res.data.plans || plans);
        setTrialDays(res.data.free_trial_days || 30);
      } catch (err) {
        console.error("[MembershipChoicePage] fetch plans failed", err);
      }
    })();
  }, []);

  // Legacy onboarding page — superseded by /membership in Phase 36.
  // Always redirect there (one-shot) regardless of state.
  useEffect(() => {
    navigate("/membership", { replace: true });
  }, [navigate]);

  const startTrial = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/payments/start-trial`);
      await refreshUser();
      toast.success(`Welcome aboard! Your ${trialDays}-day free trial has started.`);
      navigate("/verification", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start trial");
    } finally { setLoading(false); }
  };

  const subscribe = async (plan) => {
    setLoading(true);
    try {
      const origin_url = window.location.origin;
      const res = await axios.post(`${API}/payments/subscribe`, { plan, origin_url });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Subscribe failed");
      setLoading(false);
    }
  };

  const annualMonthly = (plans.annual?.price / 12).toFixed(2);
  const monthlyAnnual = (plans.monthly?.price * 12).toFixed(2);
  const savings = (monthlyAnnual - plans.annual?.price).toFixed(2);

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-8 px-4 md:px-8" data-testid="membership-choice-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#6B4E8A] text-[#8DB5A0] text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Welcome to DHDH, {user?.name?.split(" ")[0] || "Hygienist"}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#6B4E8A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Choose your path
          </h1>
          <p className="text-[#64748B] max-w-xl mx-auto">
            Try the full DHDH experience free for 30 days, or pick a paid plan to lock in your membership today. You can switch any time.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {/* Free Trial */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border-2 border-[#8DB5A0] rounded-3xl p-6 flex flex-col relative shadow-[0_8px_30px_rgba(224,122,95,0.15)]"
            data-testid="plan-trial"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8DB5A0] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Most flexible</div>
            <div className="w-12 h-12 rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-[#8DB5A0]" />
            </div>
            <h2 className="text-xl font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>30-Day Free Trial</h2>
            <p className="text-sm text-[#64748B] mb-4">No credit card. No commitment. Cancel anytime by simply not subscribing.</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-4xl font-bold text-[#6B4E8A]">$0</span>
              <span className="text-sm text-[#64748B]">/ {trialDays} days</span>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {[
                "Full access to every feature",
                "Discussions, video, mentorship",
                "Health, finance, mileage trackers",
                "We'll remind you before it ends",
              ].map(b => (
                <li key={b} className="text-sm text-[#475569] flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />{b}
                </li>
              ))}
            </ul>
            <Button onClick={startTrial} disabled={loading} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full py-6 text-base font-medium" data-testid="start-trial-btn">
              {loading ? "Starting..." : "Start free trial"}
            </Button>
          </motion.div>

          {/* Monthly */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white border border-[#E5DFEC] rounded-3xl p-6 flex flex-col"
            data-testid="plan-monthly"
          >
            <div className="w-12 h-12 rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-[#6B4E8A]" />
            </div>
            <h2 className="text-xl font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly</h2>
            <p className="text-sm text-[#64748B] mb-4">Pay-as-you-go flexibility, ideal if you're trying things out month-to-month.</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-4xl font-bold text-[#6B4E8A]">${plans.monthly?.price}</span>
              <span className="text-sm text-[#64748B]">CAD / month</span>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {[
                "Full access to every feature",
                "Cancel anytime in Profile",
                "First charge today",
                "Includes 30-day money-back",
              ].map(b => (
                <li key={b} className="text-sm text-[#475569] flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />{b}
                </li>
              ))}
            </ul>
            <Button onClick={() => subscribe("monthly")} disabled={loading} variant="outline" className="border-2 border-[#6B4E8A] text-[#6B4E8A] hover:bg-[#6B4E8A] hover:text-white rounded-full py-6 text-base font-medium transition-colors" data-testid="subscribe-monthly-btn">
              {loading ? "..." : "Subscribe monthly"}
            </Button>
          </motion.div>

          {/* Annual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-[#6B4E8A] text-white border border-[#6B4E8A] rounded-3xl p-6 flex flex-col relative"
            data-testid="plan-annual"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8DB5A0] text-[#6B4E8A] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Best value · Save ${savings}</div>
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-[#8DB5A0]" />
            </div>
            <h2 className="text-xl font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Annual</h2>
            <p className="text-sm text-white/70 mb-4">Lock in the lowest rate. Renews yearly, cancel anytime.</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold">${plans.annual?.price}</span>
              <span className="text-sm text-white/60">CAD / year</span>
            </div>
            <p className="text-xs text-[#8DB5A0] mb-5">Just ${annualMonthly}/month</p>
            <ul className="space-y-2 mb-6 flex-1">
              {[
                "Full access to every feature",
                `Save $${savings} vs. monthly`,
                "First charge today",
                "Priority support",
              ].map(b => (
                <li key={b} className="text-sm text-white/80 flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#8DB5A0] mt-0.5 shrink-0" />{b}
                </li>
              ))}
            </ul>
            <Button onClick={() => subscribe("annual")} disabled={loading} className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full py-6 text-base font-medium" data-testid="subscribe-annual-btn">
              {loading ? "..." : "Subscribe annually"}
            </Button>
          </motion.div>
        </div>

        {/* Reassurance footer */}
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="flex-1 text-sm text-[#475569]">
            <p className="font-medium text-[#6B4E8A]">After this step, we'll verify your registration</p>
            <p className="text-xs text-[#64748B]">DHDH is members-only — to keep this a safe space for licensed RDHs, you'll be asked to upload your photo ID and active practice permit. Reviews take 1-2 business days.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipChoicePage;
